/**
 * INLINE CANVAS EDITING — double-click the text on the rendered email.
 *
 * Port of the monolith's `esEditTargets` / `esTagEditable` / `esFindBind`
 * (index.html L12393–12456) — the half of the Studio that lets a division type
 * ON the email instead of hunting for the field in the side panel. Jack, on the
 * running build: "not able to edit anything else in the actual email — should be
 * able to double click as well."
 *
 * THE PROBLEM THIS SOLVES, AND WHY IT IS DONE THIS WAY
 * ----------------------------------------------------
 * The canvas paints RENDERED markup. An ECH block's renderer is
 * `html.split("{{key}}").join(escML(value))` (echRegistry L305–312), so by the
 * time the text is on screen every trace of WHICH field produced it is gone —
 * there is no marker to read back. Two candidate fixes were considered:
 *
 *   1. render a tagged variant for the canvas, wrapping each token in a span.
 *      REJECTED: a token also appears inside attributes (`href="{{btnUrl}}"`,
 *      `alt="{{imgAlt}}"`), so a blind wrap injects markup into an attribute and
 *      produces broken HTML — and the canvas would stop being byte-identical to
 *      what exports, which is the one guarantee the block canvas makes.
 *   2. the monolith's way: leave the markup alone and decorate the LIVE DOM
 *      afterwards, matching each text node back to the field value that produced
 *      it, whitespace-normalised.
 *
 * (2) is what this file does. `tagEditable` runs over the canvas node after each
 * render and wraps every matched text node in a `<span data-ek>` carrying the
 * field key — the pre-bound path, which wins before any text matching at
 * double-click time. `resolveBind` is the fallback for a node the tagger could
 * not claim.
 *
 * WHERE TEXT MATCHING IS GENUINELY UNRELIABLE (stated, not hidden)
 * ----------------------------------------------------------------
 * Two fields on one block holding the SAME value cannot be told apart by their
 * text. The monolith resolves that positionally — first unused target, in
 * document order — and this port does the same so behaviour matches, EXCEPT at
 * the fallback: `resolveBind` refuses an ambiguous match (returns null, so the
 * double-click just selects the block) rather than guessing a field. Editing the
 * wrong field silently is worse than not editing.
 *
 * Real cases in Layne's 2026-08-18 block set: ECH_4Amenities ships `label1..4`
 * all defaulted to their own label text, and ECH_ListingTable ships 10 identical
 * `homeNBeds` / `homeNBaths` values. On those, the pre-bound (positional) path
 * is what makes the edit land correctly; the fallback deliberately declines.
 *
 * Everything here is pure except `tagEditable`, which only decorates a DOM node
 * it is handed. Nothing in this file touches the export path.
 */
import { blockDef } from "../email/blocks";
import { echMeta, grClampVal } from "./echRegistry";
import { echMostlyLocked } from "./echLock";
import { echChoiceFor } from "./echChoice";
import { echDynFor, echFeatOf, echAlwaysDyn, echSqft, echBeds, echBaths, echMoney } from "./echDyn";

/** zero-width space — see `ZW` in the monolith's `esInlineOn` (L12461). Deleting
 *  all the copy makes the browser discard the editable node and the next
 *  keystroke lands outside the block (Lauren, 2026-08-04); a ZWSP keeps the node
 *  alive and is stripped from everything we read, store or send. */
export const ZW = "​";

/** strip the placeholder ZWSP from a value read out of the DOM */
export const inlineClean = (s: unknown): string => String(s ?? "").split(ZW).join("");

/**
 * The monolith's `norm` (L12394 and again at L12424), verbatim: collapse every
 * run of whitespace to one space and trim. Both sides of every comparison go
 * through it — the stored value may hold newlines that the render turned into
 * `<br>`, and the DOM may hold the indentation of the block's own markup.
 */
export const inlineNorm = (s: unknown): string => String(s ?? "").replace(/\s+/g, " ").trim();

/** One editable slot on a block: a field, or one LINE of a multi-line field. */
export interface InlineTarget {
  /** the data key the commit writes to */
  k: string;
  /** the value that produced the rendered text */
  value: string;
  /** line index within a multi-line textarea value, or -1 for the whole field */
  line: number;
}

/**
 * Is this block read-only on the canvas?
 *
 * Three states, all already modelled by the lock layer — nothing new is invented
 * here:
 *   • `echMeta().locked` — the block declares no editable field at all. That is
 *     every hero banner (the headline is typeset into the artwork), all four
 *     disclaimers, the divider, and Contact Us (echRegistry ECH_GR_PROPOSED).
 *   • `echMostlyLocked()` — ECH_MOSTLY_LOCKED, whose only editable thing is a
 *     URL. A URL is not inline-editable anyway, but say so explicitly rather
 *     than relying on that coincidence.
 *   • a legacy registry block is not locked.
 */
export function inlineLocked(type: string): boolean {
  const m = echMeta(type);
  if (!m) return false;
  return m.locked || echMostlyLocked(m.echName);
}

/**
 * ── A FEED-DRIVEN BLOCK IN DYNAMIC MODE IS NOT HAND-EDITABLE ON THE CANVAS
 * (2026-08-20, Jack: "can still edit manually in dynamic or directly onto the
 * email which needs to be fixed") ───────────────────────────────────────────────
 * In Dynamic the FEED owns the copy: the panel already replaces the fields with
 * the picker pane, but the canvas still offered every text node for inline
 * editing — so a requester could type over a fed value and watch the next fill
 * pass silently put the feed's answer back. That is the two-renderings fault with
 * a delay on it.
 *
 * Scope is deliberately narrow: ECH blocks only, Dynamic mode only, and only when
 * the feed actually OWNS fields on this block at its current level (`echDynFor`
 * non-null). A block the feed has no map for (nothing to overwrite the edit with)
 * stays editable, and Manual mode is untouched — flipping the block to Manual is
 * the sanctioned way to hand-edit it.
 */
export function inlineDynLocked(type: string, data: Record<string, unknown>): boolean {
  const m = echMeta(type);
  if (!m) return false;
  /* ── AN ALWAYS-DYNAMIC BLOCK IS ALWAYS CANVAS-LOCKED (2026-08-22) ──────────────
     The mode escape below cannot apply to a block that has no mode: the listing table
     and the Featured cards are edited in the PANEL, where a typed value claims its
     field (`fedEdit` → `__socOff`) and survives the next fill. A canvas edit has no
     such claim, so allowing it here would recreate exactly the bug this function was
     written for — type on the email, watch the fill put the feed's answer back. A
     stale `__echMode:"manual"` on an old job must not unlock it either. */
  /* ── AND UNLOCKED AGAIN, BECAUSE THE EDIT NOW CLAIMS ITS FIELD (2026-08-22) ────
     Jack: "should still be able to edit the listing table directly in the email."
     The lock above was never about the canvas being the wrong place — it was about
     a canvas edit having no way to say "I own this value", so the next fill undid
     it. `echClaimPatch` gives it the same `__socOff` claim the panel's edits have
     always had, and StudioView's inline commit routes through it, so typing a
     price on the email now survives a refill exactly as typing it in the panel
     does. The listing table and the Featured cards are therefore editable in both
     places, which is what "one mechanism, not two" was supposed to mean.
     A MODE-HAVING block in Dynamic stays locked below: that was Jack's 2026-08-20
     ask ("can still edit manually in dynamic or directly onto the email which
     needs to be fixed"), and flipping such a block to Manual is still the
     sanctioned way to hand-edit it. */
  if (echAlwaysDyn(m.echName)) return false;
  if (String((data || {}).__echMode || "dynamic") === "manual") return false;
  return !!echDynFor(m.echName, echFeatOf({ id: "", type, data }));
}

/**
 * Port of `esEditTargets` (L12414–12422), narrowed to the field kinds this repo's
 * canvas can actually resolve.
 *
 * TEXT AND TEXTAREA ONLY — which is exactly what the monolith's `esFindBind`
 * admits at top level. `url` and `image` are excluded deliberately: a URL is not
 * what the reader sees (the anchor's TEXT is a different field), and an image has
 * no text node to edit. Repeater / listrows sub-values are not covered because no
 * ECH block declares one — every ECH field is top-level (`echFields`), and the
 * Listing Table's rows are flat `homeNPlan`-style top-level keys, which ARE
 * covered.
 *
 * A multi-line textarea yields one target per non-blank line, because `escML`
 * renders each newline as a `<br>` and the browser therefore gives each line its
 * own text node. Matching the whole field against one of those lines is the bug
 * the monolith documents at L7955 — it flattened multi-line copy on the first
 * keystroke.
 */
export function inlineTargets(type: string, data: Record<string, unknown>): InlineTarget[] {
  if (inlineLocked(type) || inlineDynLocked(type, data)) return [];
  return inlineTargetsRaw(type, data);
}

/**
 * The BINDER with no lock in front of it (split out 2026-08-22).
 *
 * `inlineTargets` = "is this block editable on the canvas, and if so where"; this is
 * only the second half. Nothing in the app should call it — the lock is the point —
 * but the positional-binding tests need to exercise the binding on a block that is
 * permanently canvas-locked, and slipping past the lock with a fake `__echMode` was
 * testing a state the app can no longer be in.
 */
export function inlineTargetsRaw(type: string, data: Record<string, unknown>): InlineTarget[] {
  const def = blockDef(type);
  if (!def) return [];
  /* ── A FIELD WHOSE TOKEN ONLY EVER LANDS IN AN ATTRIBUTE IS NOT ON SCREEN ──
     `imgAlt` is declared `type:"text"`, but its token sits in `alt="{{imgAlt}}"`
     — it never produces a text node. Offering it as an inline target is not just
     useless, it is DANGEROUS: pass 1 matches on VALUE, so an alt text that
     happens to equal a visible field's value (both "Elevon", say) could claim
     that visible node and send the requester's typing into the alt attribute.
     Stripping the tags and asking whether the token survives is exact, and costs
     one regex per block. ECH only — a legacy block has no `__html` to inspect,
     and its renderer is a TS function rather than token substitution. */
  const meta = echMeta(type);
  const textOnly = meta ? String(data.__html ?? "").replace(/<[^>]*>/g, " ") : null;
  const onScreen = (k: string): boolean => {
    if (textOnly == null) return true;
    const tok = (meta && meta.declared[k] && meta.declared[k].token) || "{{" + k + "}}";
    return textOnly.indexOf(tok) > -1;
  };
  const out: InlineTarget[] = [];
  for (const f of def.fields || []) {
    const k = f.k;
    if (!k || k.indexOf("__") === 0) continue;
    if (f.type !== "text" && f.type !== "textarea") continue;
    /* ── A BUTTON LABEL IS A CHOICE, NOT COPY (Jack, 2026-08-19: "should not be
       able to update the text for the buttons though") ─────────────────────────
       Every field in `ECH_CHOICES` is rendered in the panel as a select over the
       25 approved CTA labels — that list exists precisely because free-text
       buttons produced copy that could not fit a 200px button and five names for
       the same action across 47 divisions (echChoice.ts). Inline editing writes
       raw text straight to the field, so a double-click on the button was a hole
       straight through that governance: the panel's select would then show the
       typed value under "On this block" and the approved list would have been
       bypassed with nothing to flag it.
       The whole map is consulted, not `btn1Label` alone — ECH_1CTA / ECH_GOTY1CTA
       / ECH_GOInvite1CTA use `btnLabel`, and ECH_2CTA / ECH_GOInvite2CTA declare
       two. Excluding the field here removes it from BOTH resolution paths at once:
       `tagEditable` never wraps it in a `[data-ek]` span (so the pre-bound path
       cannot find it) and `resolveBind` has no candidate to match its text
       against, so the double-click falls through to selecting the block — which
       is exactly what the requester needs, because the panel it opens is where
       the label is changed. */
    if (meta && echChoiceFor(meta.echName, k)) continue;
    if (!onScreen(k)) continue;
    const v = String(data[k] ?? "");
    if (!v.trim()) continue;
    if (f.type === "textarea" && v.indexOf("\n") > -1) {
      v.split("\n").forEach((ln, i) => { if (ln.trim()) out.push({ k, value: ln, line: i }); });
    } else {
      /* ── BIND WHAT THE CANVAS SHOWS (2026-09-25) ─────────────────────────────────
         The render clamps every value to its cap; the fill does not. A fed plan name
         longer than the cap showed truncated, never matched its stored value, and so
         never became editable. The target carries the value as rendered. */
      out.push({ k, value: meta ? grClampVal(type, k, v) : v, line: -1 });
    }
  }
  /* ── AND THE COLLECTION HEADINGS PAST THE DECLARED TWO (2026-09-25) ─────────────────
     A re-cut table carries `{{collection3Name}}`, `{{collection4Name}}`… that the block's
     fields do not declare, but the render fills them from the data all the same — so they
     are on screen and were the one heading a double-click could not reach. */
  if (meta && textOnly != null) {
    const have = new Set(out.map((t) => t.k));
    Object.keys(data).forEach((k) => {
      if (!/^collection\d+Name$/.test(k) || have.has(k)) return;
      const v = String(data[k] ?? "");
      if (!v.trim() || textOnly.indexOf("{{" + k + "}}") < 0) return;
      out.push({ k, value: v, line: -1 });
    });
  }
  return out;
}

/**
 * A typed number stored in the shape the fill writes (2026-09-25), so a canvas edit and
 * a fed value look the same on the email: "2400" → "2,400", "3.0" → "3", "2.25" → "2.5",
 * "412990" → "$412,990". Only a plain number is reshaped; anything else ("From $399,990",
 * "TBD", a range) is the requester's wording and is kept as typed. Names are never
 * re-cased here — what someone types is what they meant.
 */
export function inlineShape(k: string, v: string): string {
  const t = String(v || "").trim();
  const num = t.replace(/,/g, "");
  if (/(^|\d)SqFt$|^SqFt$/.test(k) && /^\d+(\.\d+)?$/.test(num)) return echSqft(Number(num));
  if (/Beds$/.test(k) && /^\d+(\.\d+)?$/.test(num)) return echBeds(Number(num));
  if (/Baths$/.test(k) && /^\d+(\.\d+)?$/.test(num)) return echBaths(Number(num));
  if (/Price$/.test(k) && /^\$?\d+(\.\d+)?$/.test(num)) {
    const n = Number(num.replace(/^\$/, ""));
    if (isFinite(n) && n > 0) return echMoney(n);
  }
  return v;
}

/**
 * The FALLBACK resolution (monolith `esFindBind`, L12393) — used only when the
 * double-clicked node was not pre-bound by `tagEditable`.
 *
 * DELIBERATE DEVIATION: an ambiguous text (two different field keys holding the
 * same value) returns null instead of the monolith's first match. Selecting the
 * block and making the requester use the panel is recoverable; writing their
 * headline into the wrong field is not.
 */
export function resolveBind(targets: InlineTarget[], text: string): InlineTarget | null {
  const want = inlineNorm(inlineClean(text));
  if (!want) return null;
  const hits = targets.filter((t) => inlineNorm(t.value) === want);
  if (!hits.length) return null;
  /* same key + same line is not an ambiguity, it is one slot found twice */
  const distinct = hits.filter((h, i) => hits.findIndex((x) => x.k === h.k && x.line === h.line) === i);
  return distinct.length === 1 ? distinct[0] : null;
}

/**
 * What a commit would write — the clamp included.
 *
 * THE CAP APPLIES HERE OR IT DOES NOT APPLY AT ALL. The panel clamps every write
 * (`Inspector.wr` → `clamp(v, rule)`); if this path did not, a requester could
 * type past an SFMC guardrail simply by editing on the canvas instead of in the
 * panel. It reuses `grClampVal` — the same read point (`grFor` → the block's own
 * `@GUARDRAILS`) the panel and the render use, so all three can never disagree.
 *
 * On a per-line target the WHOLE joined value is clamped, not the line, because
 * the guardrail is a budget for the field (monolith L12417: `a[idx]=x` then
 * `__grClampVal(m.type,k,a.join("\n"))`).
 */
export interface InlineCommit {
  /** the patch to hand to `onPatch` */
  patch: Record<string, string>;
  /** the full field value after clamping */
  value: string;
  /** true when the clamp actually took characters off — the flash condition */
  clamped: boolean;
}
/** splice one target's text back into the field's full value */
function spliceValue(data: Record<string, unknown>, t: InlineTarget, typed: string): string {
  if (t.line < 0) return typed;
  const arr = String(data[t.k] ?? "").split("\n");
  while (arr.length <= t.line) arr.push("");
  arr[t.line] = typed;
  return arr.join("\n");
}

export function inlineCommit(type: string, data: Record<string, unknown>, t: InlineTarget, text: string): InlineCommit {
  /* NORMALISED AT COMMIT, and only at commit. What comes back off the DOM carries
     the block markup's own indentation (`\n        Amenities`), because the value
     was substituted into pretty-printed HTML — storing that would put whitespace
     into the field that the requester never typed and that HTML collapses anyway.
     `innerText` hides this in a browser and `textContent` does not, so relying on
     the browser to tidy it would be a bug that only ever appeared under test.
     Every target is a SINGLE rendered line, so collapsing runs is lossless.
     Deliberately NOT done in `inlineDisplay`: trimming per keystroke would eat
     the space the moment you typed it. */
  const typed0 = inlineNorm(inlineClean(text));
  const typed = t.line < 0 ? inlineShape(t.k, typed0) : typed0;
  const next = spliceValue(data, t, typed);
  const value = grClampVal(type, t.k, next);
  return { patch: { [t.k]: value }, value, clamped: value !== next };
}

/**
 * What the editable node should SHOW after a keystroke — the clamp expressed
 * back at the level the user is typing at, so a per-line edit refuses the extra
 * character on that line rather than silently dropping one somewhere else.
 */
export function inlineDisplay(type: string, data: Record<string, unknown>, t: InlineTarget, text: string): { text: string; clamped: boolean } {
  const typed = inlineClean(text);
  const value = grClampVal(type, t.k, spliceValue(data, t, typed));
  const shown = t.line >= 0 ? (value.split("\n")[t.line] ?? "") : value;
  return { text: shown, clamped: shown !== typed };
}

/** the attribute a pre-bound span carries — `data-ek` matches the monolith */
export const EK_ATTR = "data-ek";
const EK_KEY = "data-ek-k";
const EK_LINE = "data-ek-ln";

/** read a pre-bound span back into a target (null when it is not one) */
export function ekTarget(el: Element | null, data: Record<string, unknown>): InlineTarget | null {
  if (!el || !el.getAttribute(EK_KEY)) return null;
  const k = el.getAttribute(EK_KEY) as string;
  const line = parseInt(el.getAttribute(EK_LINE) || "-1", 10);
  const raw = String(data[k] ?? "");
  const value = line >= 0 ? (raw.split("\n")[line] ?? "") : raw;
  return { k, value, line };
}

/** undo a previous tagging pass so the next one is not applied on top of it */
function untag(root: Element): void {
  const doc = root.ownerDocument || document;
  root.querySelectorAll("[" + EK_ATTR + "]").forEach((sp) => {
    try { sp.parentNode?.replaceChild(doc.createTextNode(sp.textContent || ""), sp); } catch { /* leave it */ }
  });
  try { (root as Element & { normalize(): void }).normalize(); } catch { /* jsdom always has it; be safe */ }
}

/**
 * Port of `esTagEditable` (L12423–12456). Wrap every text node the block's own
 * field values produced in a `<span data-ek>` so a double-click resolves to a
 * field with no text matching at all.
 *
 * TWO PASSES, exactly as the monolith runs them and for the same reason:
 *   1. a node whose ENTIRE normalised text equals a target's value. First
 *      unused target wins, which is what makes ten identical Listing Table
 *      values land on ten different fields, in order.
 *   2. what is left: a target whose value SITS INSIDE a node shared with another
 *      field — "{{community}} | {{phone}}" in the header, "{{SqFt}} / {{Beds}}"
 *      on a plan card. The node is split around the value. Values under 3
 *      characters are skipped: too short to locate safely (a bare "3" would
 *      match the first "3" anywhere in the block).
 *
 * Idempotent — a previous pass is unwrapped first, so re-running on an untouched
 * DOM is a no-op rather than a nest of spans.
 *
 * Returns how many targets got bound (the tests assert on it; nothing else does).
 */
export function tagEditable(root: Element | null, targets: InlineTarget[]): number {
  if (!root) return 0;
  untag(root);
  if (!targets.length) return 0;
  const doc = root.ownerDocument || document;
  const used: number[] = [];
  const mk = (t: InlineTarget, text: string): HTMLSpanElement => {
    const sp = doc.createElement("span");
    sp.setAttribute(EK_ATTR, "1");
    sp.setAttribute(EK_KEY, t.k);
    sp.setAttribute(EK_LINE, String(t.line));
    sp.className = "esek";
    /* the affordance, said once on hover (2026-09-25: Jack had not found that the cards
       and table edit in place) — a tooltip, not text on the surface */
    sp.title = "Double-click to edit";
    sp.textContent = text;
    return sp;
  };

  /* ── pass 1: whole-node matches ── */
  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  const nodes: Text[] = [];
  let n: Node | null;
  while ((n = walker.nextNode())) nodes.push(n as Text);
  nodes.forEach((tn) => {
    const raw = tn.nodeValue || "";
    const tx = inlineNorm(raw);
    if (!tx) return;
    for (let i = 0; i < targets.length; i++) {
      if (used.indexOf(i) > -1) continue;
      if (inlineNorm(targets[i].value) !== tx) continue;
      try {
        /* THE SPAN HOLDS THE INK, NOT THE INDENTATION. The block's markup is
           pretty-printed, so the text node is "\n        Amenities\n      ".
           Wrapping all of it would put the gold edit outline around the
           whitespace as well, and hand that whitespace back on commit. Split it
           off and wrap only the trimmed core. */
        const lead = raw.length - raw.replace(/^\s+/, "").length;
        const core = raw.trim();
        let mid = tn;
        if (lead > 0) mid = mid.splitText(lead);
        if (mid.nodeValue && mid.nodeValue.length > core.length) mid.splitText(core.length);
        mid.parentNode?.replaceChild(mk(targets[i], mid.nodeValue || ""), mid);
        used.push(i);
      } catch { /* leave the node alone */ }
      break;
    }
  });

  /* ── pass 2: fields that SHARE a text node ── */
  targets.forEach((t, i) => {
    if (used.indexOf(i) > -1) return;
    const val = String(t.value ?? "").trim();
    if (val.length < 3) return;
    const w2 = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    let node: Node | null, hit: Text | null = null, at = -1;
    while ((node = w2.nextNode())) {
      const p = (node as Text).parentNode as Element | null;
      if (p && p.getAttribute && p.getAttribute(EK_ATTR)) continue;
      const idx = String((node as Text).nodeValue || "").indexOf(val);
      if (idx > -1) { hit = node as Text; at = idx; break; }
    }
    if (!hit) return;
    try {
      const after = hit.splitText(at);   /* hit = before, after = val + tail */
      after.splitText(val.length);       /* after = exactly the value        */
      after.parentNode?.replaceChild(mk(t, after.nodeValue || ""), after);
      used.push(i);
    } catch { /* leave the node alone */ }
  });

  /* ── pass 3: SHORT values, bound by ORDINAL (2026-08-22) ──────────────────────
     Jack, on the listing table: "can only edit sq ft". Measured — 12 of 20 fields
     bound, and every miss was a value under three characters: the homesite number
     ("4"), beds ("3"), baths ("2"). Pass 2 skips those on purpose, because a bare
     "4" would match the first 4 anywhere in the block, including inside a price.
     So they bind POSITIONALLY instead: the template's token order is the DOM's
     order, so the Nth token of a value takes the Nth standalone occurrence of that
     value. Two guards keep it honest — the occurrence must be a whole word (not a
     digit inside $334,994) and a node already claimed by an earlier pass is
     skipped. Anything still ambiguous is left alone rather than guessed at. */
  /* TARGET ORDER *IS* DOM ORDER: `inlineTargetsRaw` walks the block's declared
     fields, and the block renders those fields in the same order (home1 before
     home2, Homesite before Beds inside a row). So walking the DOM once and handing
     each standalone occurrence to the FIRST still-unbound target of that value
     matches row for row, with no ordinal bookkeeping to get wrong. */
  const shortLeft = targets
    .map((t, i) => ({ t, i }))
    .filter((x) => used.indexOf(x.i) < 0
      && String(x.t.value ?? "").trim().length > 0
      && String(x.t.value ?? "").trim().length < 3);
  if (shortLeft.length) {
    const w3 = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    const queue: Text[] = [];
    let nd: Node | null;
    while ((nd = w3.nextNode())) queue.push(nd as Text);
    queue.forEach((tn) => {
      let node = tn;
      for (;;) {
        const p = node.parentNode as Element | null;
        if (p && p.getAttribute && p.getAttribute(EK_ATTR)) return;
        const text = String(node.nodeValue || "");
        let done = true;
        for (let idx = 0; idx < text.length; idx++) {
          const cand = shortLeft.find((x) => {
            if (used.indexOf(x.i) > -1) return false;
            const v = String(x.t.value ?? "").trim();
            if (text.substr(idx, v.length) !== v) return false;
            const before = idx > 0 ? text[idx - 1] : " ";
            const after = idx + v.length < text.length ? text[idx + v.length] : " ";
            /* a WHOLE word — never a digit inside "$334,994" or "2,106" */
            return !/[A-Za-z0-9.,]/.test(before) && !/[A-Za-z0-9.,]/.test(after);
          });
          if (!cand) continue;
          const v = String(cand.t.value ?? "").trim();
          try {
            const after = node.splitText(idx);        /* node = before, after = v + tail */
            const tail = after.splitText(v.length);   /* after = exactly the value        */
            after.parentNode?.replaceChild(mk(cand.t, after.nodeValue || ""), after);
            used.push(cand.i);
            node = tail;                              /* keep scanning the rest of the node */
            done = false;
          } catch { /* leave the node alone */ }
          break;
        }
        if (done) return;
      }
    });
  }

  return used.length;
}
